import React, { useContext, useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Radio } from "antd";
import {
  Wrapper,
  Header,
  Inputs,
  InputBox,
  ButtonsDiv,
  RadioInputs,
} from "./style";
import { Button } from "../../../common/Button";
import { Input } from "../../../common/Input";
import { useAppSelector } from "../../../../app/hooks";
import { FormInputs, ShareLinkModalProps } from "./types";
import ComplianceRadio from "../../../compliance/ComplianceRadio";
import { InputPhone } from "../../../common/InputPhone";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import ErrorMessage from "../../../ErrorMessage";
import { shareCustomerPaymentLink } from "../../../../app/queries/paymentLinks";
import { EMAIL_REGEX, PHONE_REGEX } from "../../../../utils/const";

const ShareLinkModal = ({
  link,
  onShareLinkSubmit,
  setShowShareModal,
  setLoading,
}: ShareLinkModalProps) => {

  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({ defaultValues: { link } });
  const { business } = useAppSelector((state) => state.settings);
  const [shareType, setShareType] = useState("email");

  const onCancel = () => {
    reset(
      {
        message: null,
        email: null,
        phone: null,
        shareType: null,
        sendValue: null,
      },
      {
        keepErrors: true,
        keepTouched: false,
      },
    );
    setShowShareModal(false);
  };

  useEffect(() => {
    if (link) {
      reset();
      setShareType("email");
    }
  }, [link, reset]);

  const onShareTypeChange = (e) => {
    setShareType(e.target.value);
  };

  const { mutate: sharePaymentLink, isLoading } = shareCustomerPaymentLink()
  setLoading(isLoading)

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    try {
      const shareVal = shareType === "email" ? data.email : data.phone;
      const sendType = shareType === "email" ? "toEmails" : "toNumbers";
      const payload = {
        message: data.message,
        link: data.link,
        sendType,
        sendValue: shareVal,
        businessName: business.name,
      }

      sharePaymentLink(payload, {
        onSuccess: () => {
          reset();
          setShareType("email");
          onShareLinkSubmit();
        }
      })
    } catch (err) {
      onShowErrorAlert(err, "Error sharing link");
    }
    
  };

  return (
    <Wrapper onSubmit={handleSubmit(onSubmit)}>
      <Header>Share payment link</Header>
      <Inputs>
        <InputBox>
          <Controller
            name="message"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Input
                type="text"
                label="Message"
                field={field}
                value={field.value}
                labelColor="#FFFFFF"
                placeholder="Enter message"
              />
            )}
          />
          {errors.message?.type === "required" && (
            <ErrorMessage message="Field is required" />
          )}
        </InputBox>
        <InputBox>
          <Controller
            name="link"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <Input
                type="text"
                label="Link"
                field={field}
                value={link}
                labelColor="#FFFFFF"
                readonly
              />
            )}
          />
        </InputBox>
        <RadioInputs>
          <Radio.Group onChange={onShareTypeChange} value={shareType}>
            <ComplianceRadio label="Email" value="email" />
            <ComplianceRadio label="Phone" value="phone" />
          </Radio.Group>
        </RadioInputs>
        {shareType === "email" && (
          <InputBox>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "Email is required",
                pattern: {
                  value: EMAIL_REGEX,
                  message: 'Please enter a valid email address.',
                },
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Email"
                  field={field}
                  value={field.value}
                  labelColor="#FFFFFF"
                  placeholder="Enter email"
                />
              )}
            />
            {errors?.email && (
              <ErrorMessage message={errors.email.message} />
            )}
          </InputBox>
        )}
        {shareType === "phone" && (
          <InputBox>
            <Controller
              name="phone"
              control={control}
              rules={{
                required: "Phone number is required",
                pattern: { value: PHONE_REGEX, message: "Invalid phone number" },
              }}
              render={({ field }) => (
                <InputPhone
                  label="Phone"
                  field={field}
                  placeholder="Enter phone"
                />
              )}
            />
            {errors?.phone && (
              <ErrorMessage message={errors.phone.message} />
            )}
          </InputBox>
        )}
      </Inputs>
      
      <ButtonsDiv>
        <Button
          label="Share"
          type="submit"
          theme="secondary"
          height="40px"
          fontSize="12px"
        />
        <Button
          label="Cancel"
          type="button"
          theme="alternate"
          height="40px"
          fontSize="12px"
          onClick={onCancel}
        />
      </ButtonsDiv>
    </Wrapper>
  );
};

export default ShareLinkModal;
